import type { ReactElement } from "react"
import { getFormattedPeriodInfo } from "@core/period-info"
import { getCurrencyCode } from "@core/pricing"
import { PriceFormat } from "@core/types"
import useDomainExtensionsByCategories from "@gatsby-plugin-onestore/hooks/useDomainExtensionsByCategories"
import useMenuQuery from "@gatsby-plugin-onestore/hooks/useMenuQuery"
import { useLocation } from "@reach/router"
import { get } from "lodash"
import { Helmet } from "react-helmet"
import { helmetJsonLdProp } from "react-schemaorg"
import type { Offer, Product, WithContext } from "schema-dts"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import isEmpty from "~/lib/isEmpty"

export default function DomainExtensionSchema(): ReactElement | null {
  const { pathname } = useLocation()
  const site = useSiteMetadataQuery()
  const { menu } = useMenuQuery()
  const fallbackLogoUrl: string = get(menu, "flatData.logo[0].url", "")
  const domainData = useDomainExtensionsByCategories()

  const matchedDomain = domainData
    .flatMap((category) => category.flatData.domains)
    .find(
      (domain) =>
        !isEmpty(domain.flatData.pages) &&
        domain.flatData.pages[0].flatData.url === pathname
    )

  if (!matchedDomain) {
    return null
  }

  const { title, extension, periods, logo } = matchedDomain.flatData
  const name = title || `.${extension}`
  const price = getFormattedPeriodInfo(
    periods,
    undefined,
    PriceFormat.REGISTER_NETTO_DOT_SEPARATED
  )

  if (!price) {
    return null
  }

  const image = !isEmpty(logo) ? logo![0].url : fallbackLogoUrl
  const pageUrl = `${site.siteUrl}${pathname}`

  const offers: Offer = {
    "@type": "Offer",
    url: pageUrl,
    priceCurrency: getCurrencyCode(),
    price,
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: {
      "@type": "Organization",
      name: site.ogSiteName,
    },
  }

  const schema: WithContext<Product> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    url: pageUrl,
    ...(image ? { image } : {}),
    brand: {
      "@type": "Brand",
      name: site.ogSiteName,
    },
    offers,
  }

  return <Helmet script={[helmetJsonLdProp(schema)]} />
}
